import type { ReactNode } from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'

import { BanknotesIcon } from '@gto/icons-react/outline/banknotes'
import { CurrencyDollarIcon } from '@gto/icons-react/outline/currency-dollar'
import { FunnelIcon } from '@gto/icons-react/outline/funnel'
import { ScaleIcon } from '@gto/icons-react/outline/scale'
import { ShoppingCartIcon } from '@gto/icons-react/outline/shopping-cart'

import { DashboardKpiTile } from '@/components/dashboard-kpi-tile'
import { EmptyState } from '@/components/empty-state'
import { StatStrip } from '@/components/stat-strip'
import { StatsCard } from '@/components/stats-card'
import type {
  AffiliateCampaignRowSchema,
  AffiliateCampaignSeriesRowSchema,
} from '@/domain/campaigns'
import { avgOrderValue, cumulate, funnelRatePct, isCampaignEmpty } from '@/domain/campaigns'
import { toAmount } from '@/utils/to-amount'

import type { CampaignChartPoint } from './campaign-chart'
import { CampaignChart } from './campaign-chart'
import { CampaignFunnel } from './campaign-funnel'

type CampaignDetailProps = {
  row: AffiliateCampaignRowSchema
  series: AffiliateCampaignSeriesRowSchema[] | null
  granularityToggle: ReactNode
  /** Partner-level, so it rides the response rather than the row. */
  partnerTierMissing?: boolean
}

const Usd = ({ value }: { value: number }) => (
  <FormattedNumber
    currency="USD"
    maximumFractionDigits={0}
    style="currency"
    value={value}
  />
)

const RatePct = ({ value }: { value: number | null }) =>
  value === null ? (
    <>—</>
  ) : (
    <>
      <FormattedNumber
        maximumFractionDigits={1}
        value={value}
      />
      %
    </>
  )

/**
 * Builds the chart's buckets, including the two derived series the raw payload
 * does not carry.
 *
 * NOT reversed, unlike admin-reports-page: campaign-stats returns its buckets
 * oldest-first already, so reversing here would put the chart backwards.
 */
const toChartPoints = (series: AffiliateCampaignSeriesRowSchema[]): CampaignChartPoint[] => {
  const revenue = series.map((point) =>
    toAmount(point.revenue_usd, 'campaign detail series revenue_usd:'),
  )
  const commissions = series.map((point) =>
    toAmount(point.commission_usd, 'campaign detail series commission_usd:'),
  )
  const cumulativeRevenue = cumulate(revenue)
  const cumulativeCommissions = cumulate(commissions)

  return series.map((point, index) => ({
    label: point.period_label,
    revenue: revenue[index] ?? 0,
    commissions: commissions[index] ?? 0,
    cumulativeRevenue: cumulativeRevenue[index] ?? 0,
    cumulativeCommissions: cumulativeCommissions[index] ?? 0,
    orders: point.orders,
    conversions: point.conversions,
  }))
}

export const CampaignDetail = ({
  row,
  series,
  granularityToggle,
  partnerTierMissing,
}: CampaignDetailProps) => {
  const { formatMessage } = useIntl()

  if (isCampaignEmpty(row)) {
    return (
      <EmptyState title={<FormattedMessage id="admin.partnerDetail.campaigns.detail.empty" />} />
    )
  }

  const chartData = toChartPoints(series ?? [])

  const revenue = toAmount(row.revenue_usd, 'campaign detail revenue_usd:')
  const commission = toAmount(row.commission_usd, 'campaign detail commission_usd:')
  const net = toAmount(row.net_usd, 'campaign detail net_usd:')

  // Net is not in the series payload, but it is exactly revenue − commission
  // per bucket, so its sparkline costs nothing extra.
  const sparkline = (pick: (point: CampaignChartPoint) => number) => chartData.map(pick)

  const aov = avgOrderValue(revenue, row.orders)

  return (
    <div
      className="flex flex-col gap-4"
      data-tst="campaign-detail"
    >
      {/* The headline. These four are the only tiles on the page that keep the
          accent treatment — everything below is supporting detail, and a page
          that shouts every number has no emphasis left to spend. */}
      <section
        className="flex flex-wrap gap-2"
        data-tst="campaign-money"
      >
        <DashboardKpiTile
          icon={<CurrencyDollarIcon />}
          label={<FormattedMessage id="admin.partnerDetail.campaigns.col.revenue" />}
          sparklineData={sparkline((point) => point.revenue)}
          tooltip={formatMessage({ id: 'admin.partnerDetail.campaigns.detail.revenueTip' })}
          value={<Usd value={revenue} />}
        />
        <DashboardKpiTile
          icon={<BanknotesIcon />}
          label={<FormattedMessage id="admin.partnerDetail.campaigns.col.commission" />}
          sparklineData={sparkline((point) => point.commissions)}
          tooltip={formatMessage({ id: 'admin.partnerDetail.campaigns.detail.commissionTip' })}
          value={<Usd value={commission} />}
        />
        <DashboardKpiTile
          icon={<ScaleIcon />}
          label={<FormattedMessage id="admin.partnerDetail.campaigns.col.net" />}
          sparklineData={sparkline((point) => point.revenue - point.commissions)}
          tooltip={formatMessage({ id: 'admin.partnerDetail.campaigns.detail.netTip' })}
          value={<Usd value={net} />}
        />
        <DashboardKpiTile
          icon={<ShoppingCartIcon />}
          label={<FormattedMessage id="admin.partnerDetail.campaigns.col.orders" />}
          sparklineData={sparkline((point) => point.orders)}
          tooltip={formatMessage({ id: 'admin.partnerDetail.campaigns.detail.ordersTip' })}
          value={<FormattedNumber value={row.orders} />}
        />
      </section>

      {/* Derived quality signals. Two campaigns can post identical revenue and
          be worth very different amounts of partner attention. */}
      <StatStrip
        data-tst="campaign-quality"
        cells={[
          {
            id: 'aov',
            label: <FormattedMessage id="admin.partnerDetail.campaigns.detail.aov" />,
            value: aov === null ? '—' : <Usd value={aov} />,
          },
          {
            id: 'customers',
            label: <FormattedMessage id="admin.partnerDetail.campaigns.detail.customers" />,
            value: <FormattedNumber value={row.unique_customers} />,
          },
          {
            id: 'commission-rate',
            label: <FormattedMessage id="admin.partnerDetail.campaigns.detail.commissionRate" />,
            value: <RatePct value={funnelRatePct(commission, revenue)} />,
          },
        ]}
      />

      <StatsCard
        icon={<FunnelIcon />}
        title={<FormattedMessage id="admin.partnerDetail.campaigns.detail.funnel" />}
      >
        <CampaignFunnel
          partnerTierMissing={partnerTierMissing}
          row={row}
        />
      </StatsCard>

      <CampaignChart
        controls={granularityToggle}
        data={chartData}
        emptyLabel={<FormattedMessage id="admin.partnerDetail.chart.empty" />}
        title={<FormattedMessage id="admin.partnerDetail.campaigns.detail.performance" />}
      />
    </div>
  )
}
